import { useEffect, useState } from "react";
import Table from "../../components/admin/Table";
import { SystemLogsService } from "../../services/SystemLogsService";
import { DateTime } from "luxon";

export default function SystemLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  const service = new SystemLogsService();

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const response = await service.listLogs();

      // Format logs for display
      const formattedLogs = response.items.map((log) => ({
        ...log,
        formattedDate: formatLogDate(log.date),
        displayName: `Logs de ${formatLogDate(log.date)}`,
      }));

      setLogs(formattedLogs);
    } catch (error) {
      console.error("Error fetching system logs:", error);
      alert("Erro ao carregar logs do sistema");
    } finally {
      setLoading(false);
    }
  };

  const formatLogDate = (dateStr) => {
    try {
      // Parse the date string (YYYY-MM-DD format)
      const date = DateTime.fromISO(dateStr, { zone: "America/Sao_Paulo" });

      if (date.isValid) {
        // Format as "23 de setembro de 2025"
        return date
          .setLocale("pt-BR")
          .toLocaleString({
            day: "numeric",
            month: "long",
            year: "numeric",
          });
      }
    } catch (error) {
      console.error("Error formatting date:", error);
    }
    return dateStr;
  };

  const handleDownload = async (log) => {
    setDownloading((prev) => ({ ...prev, [log.filename]: true }));

    try {
      const result = await service.downloadLog(log.filename);

      if (!result.success) {
        alert("Erro ao baixar o arquivo de log");
      }
    } catch (error) {
      console.error("Error downloading log:", error);
      alert("Erro ao baixar o arquivo de log");
    } finally {
      setDownloading((prev) => ({ ...prev, [log.filename]: false }));
    }
  };

  const columns = [
    { label: "Data", field: "displayName" },
    { label: "Arquivo", field: "filename" },
    { label: "Tamanho", field: "sizeFormatted" },
    {
      label: "Ações",
      field: "actions",
      render: (row) => (
        <button
          className="btn btn-sm btn-primary"
          onClick={() => handleDownload(row)}
          disabled={downloading[row.filename]}
        >
          {downloading[row.filename] ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Baixando...
            </>
          ) : (
            <>
              <i className="bi bi-download me-1"></i>
              Baixar
            </>
          )}
        </button>
      ),
    },
  ];

  if (loading) {
    return (
      <div className="text-center mt-5">Carregando logs do sistema...</div>
    );
  }

  return (
    <div className="container pt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h2>Logs do Sistema</h2>
          <small className="text-muted">
            <i className="bi bi-info-circle me-1"></i>
            Arquivos de log do sistema (.log)
          </small>
        </div>
        <button className="btn btn-outline-secondary" onClick={fetchLogs}>
          <i className="bi bi-arrow-clockwise me-1"></i>
          Atualizar
        </button>
      </div>

      {logs.length === 0 ? (
        <div className="alert alert-info">
          <i className="bi bi-info-circle me-2"></i>
          Nenhum arquivo de log encontrado.
        </div>
      ) : (
        <div className="card">
          <div className="card-body p-0">
            <Table
              columns={columns}
              data={logs}
              actions={false}
              searchable={false}
              addButton={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}